/**
 * ExpiringClearancesWidget — small dashboard card showing the next 5 employee
 * clearances within their 30-day expiration window (or already expired).
 * Clicking a row deep-links to the employee profile's Clearances tab.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, AlertTriangle, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";

const STATUS_BADGES = {
  valid: { label: "Valid", cls: "bg-[#DCFCE7] text-[#166534]", Icon: CheckCircle2 },
  expiring_soon: { label: "Soon", cls: "bg-[#FEF3C7] text-[#92400E]", Icon: AlertTriangle },
  expired: { label: "Expired", cls: "bg-[#FEE2E2] text-[#991B1B]", Icon: AlertCircle },
};

export default function ExpiringClearancesWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/clearances/expiring", { params: { days: 30, limit: 5 } });
        if (!cancelled) setItems(data?.items || []);
      } catch {
        // 403 (no employees_manage perm) is fine — just hide the widget.
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide entirely when the caller has no perm (403) or there is nothing to show.
  if (errored || (!loading && items.length === 0)) return null;

  return (
    <section
      className="bg-white rounded-2xl border border-[#2563EB]/10 shadow-floating p-5"
      data-testid="dash-expiring-clearances"
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-2xl bg-[#FEF3C7] flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#92400E]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-foreground tracking-tight">Expiring Clearances</h3>
            <p className="text-xs text-slate-500">Next 30 days · top 5</p>
          </div>
        </div>
        <Link
          to="/employees"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline"
          data-testid="dash-expiring-clearances-view-all"
        >
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </header>

      {loading ? (
        <div className="text-xs text-slate-500 text-center py-4">Loading…</div>
      ) : (
        <ul className="divide-y divide-[#2563EB]/10">
          {items.map((row) => {
            const badge = STATUS_BADGES[row.status] || STATUS_BADGES.valid;
            const Icon = badge.Icon;
            const days = row.days_until_expiration;
            const dayLabel = days < 0
              ? `${Math.abs(days)}d overdue`
              : (days === 0 ? "expires today" : `in ${days}d`);
            return (
              <li key={row.id}>
                <Link
                  to={`/employees/list?employee=${row.employee_id}&tab=clearances`}
                  className="flex items-center gap-3 py-2.5 hover:bg-brand-purpleLight/30 -mx-2 px-2 rounded-xl transition-colors"
                  data-testid={`dash-expiring-row-${row.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-brand-purpleLight flex items-center justify-center overflow-hidden shrink-0">
                    {row.employee_picture ? (
                      <img src={row.employee_picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold text-brand-purpleHover">
                        {(row.employee_name || "?").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-foreground truncate">{row.employee_name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {row.clearance_type_name} · expires {row.expiration_date}
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${badge.cls}`}>
                    <Icon className="w-3 h-3" /> {dayLabel}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
